import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface BillAttachment {
  id: string;
  bill_id: string;
  company_id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export const useBillAttachments = (billId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["bill_attachments", billId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bill_attachments")
        .select("*")
        .eq("bill_id", billId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as BillAttachment[];
    },
    enabled: !!user && !!billId,
  });
};

export const useUploadBillAttachment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      bill_id: string;
      file: File;
    }) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;
      const userId = (await supabase.auth.getUser()).data.user?.id;

      const filePath = `${companyId}/${input.bill_id}/${Date.now()}_${input.file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from("bill-attachments")
        .upload(filePath, input.file);
      if (uploadErr) throw uploadErr;

      const { error } = await supabase
        .from("bill_attachments")
        .insert({
          bill_id: input.bill_id,
          company_id: companyId,
          file_name: input.file.name,
          file_path: filePath,
          file_size: input.file.size,
          mime_type: input.file.type || null,
          uploaded_by: userId,
        } as any);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["bill_attachments", vars.bill_id] });
    },
  });
};

export const useDeleteBillAttachment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (att: BillAttachment) => {
      await supabase.storage.from("bill-attachments").remove([att.file_path]);
      const { error } = await supabase.from("bill_attachments").delete().eq("id", att.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["bill_attachments", vars.bill_id] });
    },
  });
};
